import { Classes, MenuItem } from "@blueprintjs/core";
import { ItemRenderer, MultiSelect } from "@blueprintjs/select";
import { observer } from "mobx-react";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { filterItemPredicate, matchTargetWidthPopover } from "modules/common/services/form/select";
import { texts } from "modules/common/texts";
import React from "react";
import { Schema } from "yup";
import { fields } from "../models/settings";
import { SettingsStoreType } from "../models/settings";
import styles from "./WorkloadPlanSettings.module.scss";

const PeopleSelect = MultiSelect.ofType<SelectItem>();

interface FlagOption {
    field: string;
    label: string;
    description: string;
}

const OPTIONS: FlagOption[] = [
    {
        field: fields.questionsBoardEnabled,
        label: "Системные колонки доски по проектам",
        description:
            "Доска заданий по проектам показывается с четырьмя системными колонками: «Просрочено», «В работе», " +
            "«На проверке» и «Исполнено». Переименовать и удалить эти колонки нельзя, «Просрочено» заполняется " +
            "по сроку задания — перетащить карточку в неё нельзя. Исполнители и ответственные двигают карточку " +
            "максимум до «На проверке», в «Исполнено» её переносит тот, кто выдал задание. " +
            "Выключено — доска по проектам остаётся, но состоит только из своих колонок, поэтому их добавление " +
            "включается автоматически.",
    },
    {
        field: fields.questionsCustomColumnsEnabled,
        label: "Свои колонки на доске",
        description:
            "К системным колонкам можно добавлять свои — с названием, цветом, местом на доске и признаком архива. " +
            "Набор колонок один на всю компанию: он одинаков и на странице «Трекер задач», и во вкладке любого проекта. " +
            "Настраивает сотрудник с полным доступом к заданиям.",
    },
    {
        field: fields.questionsPortfoliosEnabled,
        label: "Личные доски заданий",
        description:
            "Сотрудники заводят свои доски задач, не связанные с проектами, и приглашают в них участников. " +
            "Доска видна только владельцу и приглашённым, у неё своя нумерация заданий и свой набор колонок. " +
            "Карточки досок появляются в шапке страницы «Трекер задач».",
    },
];

/**
 * Возможности страницы «Трекер задач»: флажки независимы, включать можно любые.
 */
const QuestionsSettingsBase: React.FC<QuestionsSettingsProps> = ({ schema, store }) => {
    const people = store.employers.employee;

    return (
        <div className="columns">
            <div className={styles.options}>
                {OPTIONS.map((option) => (
                    <StandardFormInput
                        key={option.field}
                        name={option.field}
                        schema={schema}
                        small={true}
                        inline={false}
                        label=""
                    >
                        {({ field, form }) => (
                            <>
                                <div
                                    className={`${styles.option} ${field.value ? styles.selected : ""}`}
                                    onClick={() => {
                                        const value = !field.value;

                                        form.setFieldValue(field.name, value);
                                        form.setFieldTouched(field.name, true);

                                        // без системных колонок доска держится на своих: включаем их принудительно
                                        if (option.field === fields.questionsBoardEnabled && !value) {
                                            form.setFieldValue(fields.questionsCustomColumnsEnabled, true);
                                            form.setFieldTouched(fields.questionsCustomColumnsEnabled, true);
                                        }
                                    }}
                                >
                                    <PlanrButton
                                        type="neutral"
                                        icon={field.value ? "general-checkbox" : "general-zero-checkbox"}
                                        size="small"
                                    />

                                    <div className={styles.text}>
                                        <div className={styles.label}>{option.label}</div>
                                        <div className={styles.description}>{option.description}</div>
                                    </div>
                                </div>

                                {option.field === fields.questionsPortfoliosEnabled && field.value && (
                                    <PortfolioAuthors schema={schema} people={people} />
                                )}
                            </>
                        )}
                    </StandardFormInput>
                ))}
            </div>
        </div>
    );
};

/**
 * Кому разрешено заводить свои доски. Приглашённых это не касается: присоединиться
 * к чужой доске можно и без этого права
 */
const PortfolioAuthors: React.FC<{ schema: Schema<any>; people: SelectItem[] }> = ({ schema, people }) => {
    // выбранных отмечаем галочкой, а подсветку пункта под клавиатурой делаем серой:
    // синий фон у первого пункта читался как «уже выбран»
    const renderPerson = (selected: string[]): ItemRenderer<SelectItem> => (item, { handleClick, modifiers }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        return (
            <MenuItem
                key={item.id}
                className={modifiers.active ? styles.optionActive : ""}
                disabled={modifiers.disabled}
                icon={selected.includes(item.id) ? "tick" : "blank"}
                onClick={handleClick}
                shouldDismissPopover={false}
                text={item.label || texts.undefined}
            />
        );
    };

    return (
        <div className={styles.suboption}>
            <div className={styles.subtitle}>Кому разрешено заводить свои доски</div>

            <StandardFormInput
                name={fields.questionsPortfolioAuthors}
                schema={schema}
                small={true}
                inline={false}
                label=""
            >
                {({ field, form }) => {
                    const selected: string[] = field.value || [];

                    const items = selected
                        .map((id: string) => people.find((p) => p.id === id))
                        .filter((p) => !!p) as SelectItem[];

                    const change = (next: string[]) => {
                        form.setFieldValue(field.name, next);
                        form.setFieldTouched(field.name, true);
                    };

                    return (
                        <PeopleSelect
                            className={`full-width-select ${Classes.FILL}`}
                            itemRenderer={renderPerson(selected)}
                            itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
                            selectedItems={items}
                            items={people}
                            onItemSelect={(item: SelectItem) =>
                                change(
                                    selected.includes(item.id)
                                        ? selected.filter((id: string) => id !== item.id)
                                        : [...selected, item.id]
                                )
                            }
                            placeholder="Кто может заводить доски"
                            itemPredicate={filterItemPredicate}
                            tagRenderer={(item: SelectItem) => item.label}
                            noResults={<MenuItem disabled={true} text={texts.noData} />}
                            popoverProps={matchTargetWidthPopover()}
                            // после выбора строку поиска сбрасываем: следующего ищут с чистого листа
                            resetOnSelect={true}
                            fill={true}
                            tagInputProps={{
                                onRemove: (_value: any, index: number) =>
                                    change(selected.filter((_: string, i: number) => i !== index)),
                            }}
                        />
                    );
                }}
            </StandardFormInput>
        </div>
    );
};

interface QuestionsSettingsProps {
    schema: Schema<any>;
    store: SettingsStoreType;
}

export const QuestionsSettings = observer(QuestionsSettingsBase);
